import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	ButtonBase,
	CircularProgress,
	ClickAwayListener,
	InputAdornment,
	InputBase,
	Paper,
	Popper,
	Typography,
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';

const DEFAULT_PLACEHOLDER = '-';
const DEFAULT_SEARCH_PLACEHOLDER = 'Search…';
const DEFAULT_EMPTY_MESSAGE = 'No results';

const Caret = () => (
	<Box
		sx={{
			width: 6,
			height: 6,
			borderRight: '1.5px solid',
			borderBottom: '1.5px solid',
			borderColor: 'text.tertiary',
			transform: 'rotate(45deg)',
			mt: '-2px',
			flexShrink: 0,
		}}
	/>
);

const PinnedTag = () => (
	<Box
		component='span'
		sx={{
			fontSize: '9px',
			fontWeight: 600,
			color: 'status.blue.text',
			backgroundColor: 'status.blue.bg',
			border: '0.5px solid',
			borderColor: 'status.blue.border',
			borderRadius: '8px',
			padding: '1px 6px',
			flexShrink: 0,
		}}
	>
		Pinned
	</Box>
);

const SearchPicker = ({
	options = [],
	selected,
	pinnedId,
	placeholder = DEFAULT_PLACEHOLDER,
	searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
	emptyMessage = DEFAULT_EMPTY_MESSAGE,
	searchValue,
	onChangeSearchValue,
	onSelect,
	onPinToggle,
	loading = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const triggerRef = useRef(null);

	const selectedId = selected?.id;
	const isSelectedPinned = Boolean(
		selectedId && pinnedId && selectedId === pinnedId
	);

	const handleToggle = (event) => {
		event.stopPropagation();
		onChangeSearchValue('');
		setIsOpen((open) => !open);
	};

	const handleSelect = (option) => {
		setIsOpen(false);
		onChangeSearchValue('');
		onSelect(option);
	};

	const handlePinClick = (event) => {
		event.stopPropagation();
		if (onPinToggle && selected) {
			onPinToggle(selected);
		}
	};

	return (
		<ClickAwayListener onClickAway={() => setIsOpen(false)}>
			<Box display='flex' alignItems='center' sx={{ gap: '6px', position: 'relative' }}>
				<ButtonBase
					ref={triggerRef}
					onClick={handleToggle}
					disableRipple
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '5px',
						padding: '2px 4px',
						margin: '-2px -4px',
						borderRadius: '6px',
						backgroundColor: 'transparent',
						'&:hover': { backgroundColor: 'background.subtle' },
					}}
				>
					<Typography
						sx={{
							fontSize: '13px',
							fontWeight: 600,
							color: 'text.primary',
						}}
					>
						{selected?.name || placeholder}
					</Typography>
					<Caret />
				</ButtonBase>

				{onPinToggle && selected && (
					<ButtonBase
						onClick={handlePinClick}
						disableRipple
						title={
							isSelectedPinned
								? 'Unpin as default'
								: 'Pin as default'
						}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '4px',
							fontSize: '11px',
							fontWeight: 500,
							color: isSelectedPinned ? 'status.blue.text' : 'text.secondary',
							backgroundColor: isSelectedPinned
								? 'status.blue.bg'
								: 'transparent',
							border: '0.5px solid',
							borderColor: isSelectedPinned ? 'primary.main' : 'border.main',
							borderRadius: '6px',
							padding: '4px 9px',
							'&:hover': {
								backgroundColor: 'background.subtle',
								color: 'text.primary',
							},
						}}
					>
						<PushPinIcon
							sx={{ width: 12, height: 12, flexShrink: 0 }}
							fontSize='inherit'
						/>
						{isSelectedPinned ? 'Pinned' : 'Pin'}
					</ButtonBase>
				)}

				<Popper
					open={isOpen}
					anchorEl={triggerRef.current}
					placement='bottom-start'
					disablePortal
					sx={{ zIndex: 410 }}
				>
					<Paper
						elevation={0}
						sx={{
							width: 270,
							backgroundColor: 'background.paper',
							border: '0.5px solid',
							borderColor: 'border.main',
							borderRadius: '10px',
							overflow: 'hidden',
							boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
						}}
					>
						<InputBase
							autoFocus
							placeholder={searchPlaceholder}
							value={searchValue}
							onChange={(event) => onChangeSearchValue(event.target.value)}
							fullWidth
							endAdornment={
								loading ? (
									<InputAdornment position='end'>
										<CircularProgress
											size={12}
											sx={{ color: 'text.tertiary' }}
										/>
									</InputAdornment>
								) : null
							}
							sx={{
								fontSize: '12px',
								padding: '9px 12px',
								backgroundColor: 'background.subtle',
								borderBottom: '0.5px solid',
								borderColor: 'border.light',
								'& input': { padding: 0 },
								'& input::placeholder': {
									color: 'text.tertiary',
									opacity: 1,
								},
							}}
						/>
						<Box
							sx={{
								maxHeight: 190,
								overflowY: 'auto',
							}}
						>
							{options.length === 0 ? (
								<Typography
									sx={{
										padding: '14px',
										fontSize: '12px',
										color: 'text.secondary',
										textAlign: 'center',
									}}
								>
									{emptyMessage}
								</Typography>
							) : (
								options.map((option) => {
									const isSelected = option.id === selectedId;
									const isPinned = option.id === pinnedId;

									return (
										<Box
											key={option.id}
											onClick={() => handleSelect(option)}
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												gap: '8px',
												padding: '8px 12px',
												cursor: 'pointer',
												fontSize: '12px',
												color: isSelected
													? 'status.blue.text'
													: 'text.primary',
												backgroundColor: isSelected
													? 'status.blue.bg'
													: 'transparent',
												fontWeight: isSelected ? 600 : 400,
												'&:hover': {
													backgroundColor: isSelected
														? 'status.blue.bg'
														: 'background.subtle',
												},
											}}
										>
											<Typography
												sx={{
													fontSize: '12px',
													fontWeight: 'inherit',
													color: 'inherit',
												}}
											>
												{option.name}
											</Typography>
											{isPinned && <PinnedTag />}
										</Box>
									);
								})
							)}
						</Box>
					</Paper>
				</Popper>
			</Box>
		</ClickAwayListener>
	);
};

SearchPicker.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string.isRequired,
		})
	),
	selected: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		name: PropTypes.string.isRequired,
	}),
	pinnedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	placeholder: PropTypes.string,
	searchPlaceholder: PropTypes.string,
	emptyMessage: PropTypes.string,
	searchValue: PropTypes.string.isRequired,
	onChangeSearchValue: PropTypes.func.isRequired,
	onSelect: PropTypes.func.isRequired,
	onPinToggle: PropTypes.func,
	loading: PropTypes.bool,
};

export default SearchPicker;
